import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2, Clock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import SectionLabel from "../components/SectionLabel";
import { submitContact } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", company: "", phone: "",
    query: "", preferred_datetime: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const h = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.query) {
      toast.error("Please fill in your name, email, and your query.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setSuccess(true);
      toast.success("Thank you. We'll respond within one business day.");
      setForm({ first_name: "", last_name: "", email: "", company: "", phone: "", query: "", preferred_datetime: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#050505] text-white min-h-screen">
      <section className="pt-32 pb-16 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>Inquire for Partnership</SectionLabel>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter mt-6 leading-[1.05] max-w-4xl">
            Let's <span className="text-[#3B82F6]">architect</span><br />your next move.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl">
            Connect with our team to discuss your operational needs at <a href="mailto:communications@operexagennex.com" className="text-white underline">communications@operexagennex.com</a>. Typical response time is within one business day.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-7 border border-white/10 bg-[#0a0a0c] p-8 lg:p-10"
          >
            <h2 className="font-display text-2xl font-semibold mb-6">Send us a message</h2>
            {success ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle2 className="w-14 h-14 text-[#3B82F6] mx-auto mb-4" />
                <h3 className="font-display text-xl mb-2">Inquiry received</h3>
                <p className="text-zinc-400 text-sm max-w-sm mx-auto">Thanks for reaching out. Our team has been notified and will respond within one business day.</p>
                <Button onClick={() => setSuccess(false)} className="mt-6 bg-[#3B82F6] hover:bg-[#60A5FA] rounded-none" data-testid="contact-new-btn">
                  Send another
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="First name *">
                    <Input value={form.first_name} onChange={h("first_name")} required data-testid="contact-input-first" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
                  </Field>
                  <Field label="Last name">
                    <Input value={form.last_name} onChange={h("last_name")} data-testid="contact-input-last" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
                  </Field>
                  <Field label="Email *">
                    <Input type="email" value={form.email} onChange={h("email")} required data-testid="contact-input-email" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
                  </Field>
                  <Field label="Phone">
                    <Input value={form.phone} onChange={h("phone")} data-testid="contact-input-phone" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
                  </Field>
                  <Field label="Company name">
                    <Input value={form.company} onChange={h("company")} data-testid="contact-input-company" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
                  </Field>
                  <Field label="Convenient date/time">
                    <Input value={form.preferred_datetime} onChange={h("preferred_datetime")} placeholder="e.g., 25 Mar 2026, 3:00 PM IST" data-testid="contact-input-datetime" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
                  </Field>
                </div>
                <Field label="Your Query *">
                  <Textarea rows={5} value={form.query} onChange={h("query")} required data-testid="contact-input-query" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-[#3B82F6]" />
                </Field>
                <Button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="w-full bg-[#3B82F6] hover:bg-[#60A5FA] rounded-none h-11 font-semibold">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4 mr-2" /> Submit</>}
                </Button>
              </form>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-5 space-y-6"
          >
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase">Direct Channels</div>
            {[
              { Icon: Mail, label: "Business Email", val: "communications@operexagennex.com", href: "mailto:communications@operexagennex.com" },
              { Icon: Phone, label: "Phone / WhatsApp", val: "+91 94778 56303", href: "tel:+919477856303" },
              { Icon: MapPin, label: "Virtual Office Address", val: "Space Nex - 361, Kalitala Road, North Pubachal, Haltu (Near Acropolis Mall), Kolkata, West Bengal - 700078." },
              { Icon: Clock, label: "Response Protocol", val: "Expect a response within one business day." },
            ].map(({ Icon, label, val, href }) => (
              <div key={label} className="border border-white/10 bg-[#0a0a0c] p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="w-4 h-4 text-[#3B82F6]" />
                  <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">{label}</div>
                </div>
                {href ? (
                  <a href={href} className="text-white hover:text-[#3B82F6] break-all">{val}</a>
                ) : (
                  <p className="text-zinc-300 text-sm leading-relaxed">{val}</p>
                )}
              </div>
            ))}

            <div className="border border-white/10 bg-[#0a0a0c] p-6">
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase mb-3">Global Delivery from Kolkata</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Our strategic hub at Space Nex operates as a virtual nexus, providing seamless service coverage across global time zones through secure digital infrastructure.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-zinc-300 text-xs uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
